import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Eye, RefreshCw, ExternalLink, AlertTriangle, FileEdit,
  Facebook, Mail, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const Stat = ({ label, value, tone = 'slate', testId }) => (
  <div
    className={`rounded-xl border p-4 border-${tone}-200 bg-${tone}-50/40`}
    data-testid={testId}
  >
    <p className={`text-[10px] font-bold uppercase tracking-[0.14em] text-${tone}-700`}>{label}</p>
    <p className={`text-3xl font-bold text-${tone}-900 mt-1 tabular-nums`}>{value}</p>
  </div>
);

const MillWatchPage = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, d, e, r] = await Promise.all([
        axios.get(`${API}/api/admin/mill/posts?limit=50`, { headers: auth() }),
        axios.get(`${API}/api/admin/mill/drafts?limit=50`, { headers: auth() }),
        axios.get(`${API}/api/admin/mill/emails?limit=20`, { headers: auth() }),
        axios.get(`${API}/api/admin/mill/runs?limit=10`, { headers: auth() }),
      ]);
      setPosts(p.data || []);
      setDrafts(d.data || []);
      setEmails(e.data || []);
      setRuns(r.data || []);
    } catch (err) {
      toast({
        title: 'Load failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const checkNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/mill/check-now?days_back=4&limit=15`,
        {},
        { headers: auth() },
      );
      toast({
        title: 'Mill check complete',
        description: `Scraped ${data.scraped} · ${data.new_posts} new · ${data.drafts_created} drafts` + (
          data.mentions?.length ? ` · ${data.mentions.length} ⚠️ mention(s)` : ''
        ),
      });
      await load();
    } catch (err) {
      toast({
        title: 'Check failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mentionsCount = posts.filter((p) => p.mention_match).length;
  const lastRun = runs[0];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="mill-watch-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Eye size={22} className="text-sky-500" /> Mill Magazine Watch
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Daily 08:00 UK check of{' '}
            <a
              href="https://www.facebook.com/millrenfrewshire/"
              target="_blank" rel="noreferrer"
              className="text-sky-600 hover:underline inline-flex items-center gap-1"
            >
              facebook.com/millrenfrewshire <ExternalLink size={12} />
            </a>{' '}
            — logs new posts, auto-drafts suitable ones in Brian's voice, flags any mention.
          </p>
        </div>
        <Button
          onClick={checkNow}
          disabled={running}
          className="bg-sky-500 hover:bg-sky-400 text-white"
          data-testid="mill-check-now-btn"
        >
          {running ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
          {running ? 'Checking…' : 'Check Mill now'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <Stat label="Logged posts" value={posts.length} tone="slate" testId="stat-posts" />
        <Stat label="Auto-drafts" value={drafts.length} tone="sky" testId="stat-drafts" />
        <Stat label="⚠️ Mentions" value={mentionsCount} tone={mentionsCount ? 'red' : 'slate'} testId="stat-mentions" />
        <Stat label="Email drafts" value={emails.length} tone="amber" testId="stat-emails" />
      </div>

      {lastRun && (
        <p className="text-xs text-slate-400 mt-3">
          Last run {fmt(lastRun.started_at)} — scraped {lastRun.scraped}, {lastRun.new_posts} new, {lastRun.drafts_created} drafts.
        </p>
      )}

      {/* Mentions banner */}
      {mentionsCount > 0 && (
        <div
          className="mt-6 rounded-xl border border-red-200 bg-red-50/60 p-4 flex gap-3"
          data-testid="mill-mentions-banner"
        >
          <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-red-800">
            <strong>{mentionsCount}</strong> Mill post{mentionsCount === 1 ? '' : 's'} reference{mentionsCount === 1 ? 's' : ''} you or your brands.
            {emails.length > 0 && <> A draft alert email is queued in <em>Email drafts</em> below.</>}
          </div>
        </div>
      )}

      {/* Logged Mill posts */}
      <section className="mt-8">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Facebook size={18} className="text-blue-600" /> Recent Mill posts
        </h2>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : posts.length === 0 ? (
          <p className="text-sm text-slate-500 italic" data-testid="mill-posts-empty">
            No Mill posts logged yet. Hit "Check Mill now" to fetch the last few days.
          </p>
        ) : (
          <div className="space-y-2" data-testid="mill-posts-list">
            {posts.map((p) => (
              <div
                key={p.id}
                className={`border rounded-lg p-3 bg-white ${p.mention_match ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
                data-testid={`mill-post-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="text-sm text-slate-900 font-medium min-w-0 flex-1">
                    {p.finding}
                  </div>
                  <div className="flex gap-2 items-center shrink-0">
                    <a
                      href={p.post_url}
                      target="_blank" rel="noreferrer"
                      className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1"
                    >
                      Open <ExternalLink size={11} />
                    </a>
                    {p.drafted_post_id && (
                      <span className="text-[10px] uppercase font-bold tracking-wider text-sky-700 bg-sky-100 px-2 py-1 rounded">
                        Drafted
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {p.type} · {fmt(p.published_at)} · likes {p.likes} · comments {p.comments} · shares {p.shares}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Auto-drafts */}
      <section className="mt-8">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
          <FileEdit size={18} className="text-sky-600" /> Auto-drafted for paisley.org.uk
        </h2>
        {drafts.length === 0 ? (
          <p className="text-sm text-slate-500 italic" data-testid="mill-drafts-empty">No drafts yet.</p>
        ) : (
          <div className="space-y-2" data-testid="mill-drafts-list">
            {drafts.map((d) => (
              <div
                key={d.id}
                className="border border-slate-200 rounded-lg p-3 bg-white flex items-start justify-between gap-3 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900">{d.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {d.mill_category_label || d.category} · scheduled {fmt(d.scheduled_at)}
                    {d.mill_angle && <> · <em>{d.mill_angle}</em></>}
                  </p>
                </div>
                <a
                  href={`/admin/posts/${d.id}/edit`}
                  className="text-xs text-sky-600 hover:underline"
                >
                  Review & edit
                </a>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Email drafts */}
      {emails.length > 0 && (
        <section className="mt-8">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
            <Mail size={18} className="text-amber-600" /> Alert email drafts
          </h2>
          <div className="space-y-2" data-testid="mill-emails-list">
            {emails.map((e) => (
              <details
                key={e.id}
                className="border border-amber-200 rounded-lg p-3 bg-amber-50/40"
              >
                <summary className="cursor-pointer text-sm font-medium text-slate-900">
                  {e.subject} — {fmt(e.created_at)}
                </summary>
                <div
                  className="mt-2 text-xs text-slate-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: e.body_html || '' }}
                />
                <p className="text-[11px] text-slate-500 mt-2">To: {e.to} · Status: {e.status} (not sent)</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default MillWatchPage;
